import React from 'react';
import Icon from '@mdi/react';
import { mdiPlus, mdiCheckAll, mdiEmailOutline, mdiCalendarToday, mdiRefresh, mdiMedicalBag, mdiArrowLeft, mdiArrowExpand, mdiArrowCollapse } from '@mdi/js';
import './ManageBookingsHeader.css';

/**
 * ManageBookingsHeader - Dual-mode header for bookings and messages management
 *
 * BOOKINGS MODE: Shows booking actions (add manual, confirm all, messages, today, refresh, sick day)
 * MESSAGES MODE: Shows message actions (back to bookings, filter toggle, message count, refresh)
 */
const ManageBookingsHeader = ({
  // Mode control
  viewMode = 'bookings', // 'bookings' | 'messages'

  // Bookings mode props
  selectedDate,
  pendingCount,
  totalBookingsCount,
  monthBookingPercentage,
  unreadMessageCount = 0,
  onAddManualBooking,
  onConfirmAll,
  onOpenMessages,
  onGoToToday,
  onSickDay,

  // Messages mode props
  onBackToBookings,
  showOnlyUnanswered = false,
  onToggleFilter,
  allMessageCount = 0,
  filteredMessageCount = 0,

  // Shared props
  onRefresh,

  // Calendar collapse toggle
  calendarCollapsed = false,
  onToggleCalendar
}) => {
  return (
    <div className="manage-bookings-header">
      <div className="quick-actions-status-area">
        {viewMode === 'bookings' ? (
          <>
            {/* BOOKINGS MODE — single action row */}
            <div className="action-row">
              {/* Primary actions */}
              <button className="btn-add-manual" onClick={onAddManualBooking} title="Add a manual booking entry">
                <Icon path={mdiPlus} size={0.8} />
                Add
              </button>

              <button
                className="btn-confirm-all-quick"
                onClick={onConfirmAll}
                title="Confirm all pending bookings"
                style={{ position: 'relative' }}
              >
                <Icon path={mdiCheckAll} size={0.8} />
                Confirm
                {pendingCount > 0 && (
                  <span className="message-badge">{pendingCount}</span>
                )}
              </button>

              {/* Ghost actions */}
              <button
                className="btn-messages btn-messages--ghost"
                onClick={onOpenMessages}
                aria-label="Messages"
                title="Go to manage all messages"
                style={{ position: 'relative' }}
              >
                <Icon path={mdiEmailOutline} size={0.8} />
                Messages
                {unreadMessageCount > 0 && (
                  <span className="message-badge">{unreadMessageCount}</span>
                )}
              </button>

              {onGoToToday && (
                <button
                  className="btn-today"
                  onClick={onGoToToday}
                  aria-label="Today"
                  title="Filter on today's date"
                >
                  <Icon path={mdiCalendarToday} size={0.8} />
                  Today
                </button>
              )}

              {onRefresh && (
                <button
                  className="btn-refresh"
                  onClick={onRefresh}
                  aria-label="Refresh"
                  title="Refresh bookings and availability"
                >
                  <Icon path={mdiRefresh} size={0.8} />
                </button>
              )}

              {/* Sick Day button — soft default, red on hover */}
              {onSickDay && (
                <button
                  className="btn-sick-day"
                  onClick={onSickDay}
                  aria-label="Mark sick day"
                  title="Block today and cancel bookings"
                >
                  <Icon path={mdiMedicalBag} size={0.8} />
                </button>
              )}

              {/* Calendar toggle — pushed to far right */}
              {onToggleCalendar && (
                <button
                  className="btn-toggle-calendar"
                  onClick={onToggleCalendar}
                  aria-label={calendarCollapsed ? "Expand calendar" : "Collapse calendar"}
                  title={calendarCollapsed ? "Expand calendar — day view" : "Collapse calendar — week view"}
                  style={{ marginLeft: 'auto' }}
                >
                  <Icon path={calendarCollapsed ? mdiArrowCollapse : mdiArrowExpand} size={0.8} />
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* MESSAGES MODE — single action row */}
            <div className="action-row-top">
              <button
                className="btn-back-to-bookings"
                onClick={onBackToBookings}
                aria-label="Back to Bookings"
              >
                <Icon path={mdiArrowLeft} size={0.8} />
                Back to Bookings
              </button>

              {onRefresh && (
                <button
                  className="btn-refresh"
                  onClick={onRefresh}
                  aria-label="Refresh"
                  title="Refresh messages"
                >
                  <Icon path={mdiRefresh} size={0.8} />
                </button>
              )}

              {/*
                Filter toggle retired — the always-visible "Unread" strip at
                the top of MessagesView makes it redundant. Markup preserved
                in case we revisit. Re-wire `showOnlyUnanswered` state +
                related count props in MessagesView if reintroducing.

              <button
                className={`filter-toggle-btn ${!showOnlyUnanswered ? 'active' : ''}`}
                onClick={() => onToggleFilter && onToggleFilter(false)}
              >
                All ({allMessageCount})
              </button>
              <button
                className={`filter-toggle-btn ${showOnlyUnanswered ? 'active' : ''}`}
                onClick={() => onToggleFilter && onToggleFilter(true)}
              >
                Unread ({unreadMessageCount})
              </button>
              */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManageBookingsHeader;
